import classNames from "classnames";
import React, { useCallback, useLayoutEffect, useRef } from "react";
import styles from "../../index.module.css";
import { timeFromEvent } from "../../logic/utils";
import { TimelineTime } from "../../types";
import { TIMELINE_CONTENT_OFFSET } from "./trackTheme";

type ActiveTimeMarkerProps = {
  activeTime: TimelineTime;
  scrollContainer: HTMLDivElement | null;
  totalDuration: TimelineTime;
  pixelsPerMs: number;
  scrollLeft?: number;
  onDrag?: (time: TimelineTime) => void;
};

export const ActiveTimeMarker = React.memo(function ActiveTimeMarker({
  activeTime,
  scrollContainer,
  totalDuration,
  pixelsPerMs,
  scrollLeft = 0,
  onDrag,
}: ActiveTimeMarkerProps) {
  const markerRef = useRef<HTMLDivElement | null>(null);

  // rAF 合帧：pointermove 再密也每帧只调用一次 onDrag
  const dragRafRef = useRef<number | null>(null);
  const pendingDragTimeRef = useRef<TimelineTime | null>(null);

  const updatePosition = useCallback(
    (time: TimelineTime, scale: number) => {
      const node = markerRef.current;
      if (!node) return;
      const x = time * scale + TIMELINE_CONTENT_OFFSET - scrollLeft;
      node.style.transform = `translateX(${x}px) translateX(-50%)`;
    },
    [scrollLeft]
  );

  // 用 layout effect，避免渲染后再改 transform 导致的轻微抖动
  useLayoutEffect(() => {
    updatePosition(activeTime, pixelsPerMs);
  }, [activeTime, pixelsPerMs, scrollLeft, updatePosition]);

  const flushDrag = useCallback(() => {
    dragRafRef.current = null;
    const next = pendingDragTimeRef.current;
    pendingDragTimeRef.current = null;
    if (next != null) onDrag?.(next);
  }, [onDrag]);

  const scheduleDrag = useCallback(
    (next: TimelineTime) => {
      pendingDragTimeRef.current = next;
      if (dragRafRef.current != null) return;
      dragRafRef.current = requestAnimationFrame(flushDrag);
    },
    [flushDrag]
  );

  const computeTimeFromPointerEvent = useCallback(
    (ev: PointerEvent) => {
      return timeFromEvent(
        ev as MouseEvent,
        scrollContainer,
        totalDuration,
        pixelsPerMs,
        TIMELINE_CONTENT_OFFSET
      );
    },
    [scrollContainer, totalDuration, pixelsPerMs]
  );

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();

      // 捕获指针，确保移出元素也不会丢 move/up
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // 有些环境可能不支持，忽略即可（仍然能工作，只是更容易丢事件）
      }

      const ev = event.nativeEvent;

      // 立刻计算一次（按下就能定位，不用等 move）
      const first = computeTimeFromPointerEvent(ev);
      if (first != null) scheduleDrag(first);

      const onMove = (e: PointerEvent) => {
        const next = computeTimeFromPointerEvent(e);
        if (next != null) scheduleDrag(next);
      };

      const onUpOrCancel = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUpOrCancel);
        window.removeEventListener("pointercancel", onUpOrCancel);

        if (dragRafRef.current != null) {
          cancelAnimationFrame(dragRafRef.current);
          dragRafRef.current = null;
        }

        const finalTime = pendingDragTimeRef.current;
        pendingDragTimeRef.current = null;
        if (finalTime != null) {
          onDrag?.(finalTime);
        }
      };

      window.addEventListener("pointermove", onMove, { passive: true });
      window.addEventListener("pointerup", onUpOrCancel, { passive: true });
      window.addEventListener("pointercancel", onUpOrCancel, { passive: true });
    },
    [computeTimeFromPointerEvent, scheduleDrag, onDrag]
  );

  return (
    <div
      ref={markerRef}
      className="absolute z-10 top-0 bottom-0 w-2 cursor-ew-resize will-change-transform touch-none select-none"
      style={{ left: 0 }}
      aria-hidden
      onPointerDown={handlePointerDown}
    >
      <div
        className={classNames(
          "w-px h-full mx-auto bg-text-primary cursor-ew-resize",
          styles.timeMaker
        )}
      ></div>
    </div>
  );
});
